import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, FormControlLabel, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, CircularProgress, Stack, Typography, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { fetchJobseekerNumbers, updateJobseekerNumber, addJobseekerNumber, deleteJobseekerNumber } from '../../lib/messageUtils';
import { IProps } from './types';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OptOutConfirmation from './OptOutConfirmation';
import OptInConfirmation from './OptInConfirmation';

interface PhoneNumber {
  id: string;
  number: string;
  countryCode: string;
  type: string;
  isOptedIn: boolean;
}

const Notifications = (props: IProps) => {
  const { messagingUserId, openPopIn, onClose } = props;

  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCountryCode, setNewCountryCode] = useState('+1');
  const [newNumber, setNewNumber] = useState('');
  const [newOptIn, setNewOptIn] = useState(false);
  const [confirmOptOutOpen, setConfirmOptOutOpen] = useState(false);
  const [idToOptOut, setIdToOptOut] = useState<string | null>(null);
  const [confirmOptInOpen, setConfirmOptInOpen] = useState(false);
  const [idToOptIn, setIdToOptIn] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);

  const [newType, setNewType] = useState("mobile");
  const [typeOpen, setTypeOpen] = useState(false);


  const options = [
    { value: "mobile", label: "Mobile" },
    { value: "home", label: "Home" },
    { value: "work", label: "Work" },
  ];

  useEffect(() => {
    const fetchNumbers = async () => {
      if (!openPopIn || !messagingUserId) return;

      setLoading(true);
      const data = await fetchJobseekerNumbers(messagingUserId);

      const formatted = data.map((item: any) => ({
        id: item.PhoneID,
        number: item.PhoneNumber.Number,
        countryCode: item.PhoneNumber.CountryCode,
        type: item.PhoneNumber.Type,
        isOptedIn: item.SmsOptIn,
      }));

      setPhoneNumbers(formatted);
      setLoading(false);
    };

    fetchNumbers();
  }, [openPopIn, messagingUserId]);

  useEffect(() => {
    if (addDialogOpen) {
      setNewType('mobile');
      setNewCountryCode('+1');
      setNewNumber('1');
      setNewOptIn(false);
    }
  }, [addDialogOpen]);

  const handleToggle = async (id: string) => {
    const targetPhone = phoneNumbers.find((p) => p.id === id);
    if (!targetPhone) return;

    if (targetPhone.isOptedIn) {
      setIdToOptOut(id);
      setConfirmOptOutOpen(true);
      return;
    } else {
      setIdToOptIn(id);
      setConfirmOptInOpen(true);
      return;
    }
  };


  const handleAddNewNumber = async () => {
    if (!newNumber.trim()) return;

    const dialCodeDigits = newCountryCode.replace('+', '');

    let cleanedNumber = newNumber
      .replace(/^\+/, '')
      .replace(new RegExp(`^${dialCodeDigits}`), '')
      .trim();

    cleanedNumber = cleanedNumber.replace(/^0+/, '');

    const payload = {
      phoneNumber: {
        type: newType,
        countryCode: newCountryCode,
        number: cleanedNumber,
      },
      smsOptIn: newOptIn,
      active: true,
    };

    const result = await addJobseekerNumber(messagingUserId, payload);
    if (result) {
      setAddDialogOpen(false);
      setNewNumber('');
      setNewType('mobile');
      setNewCountryCode('+1');
      setNewOptIn(true);

      const data = await fetchJobseekerNumbers(messagingUserId);
      const formatted = data.map((item: any) => ({
        id: item.PhoneID,
        number: item.PhoneNumber.Number,
        countryCode: item.PhoneNumber.CountryCode,
        type: item.PhoneNumber.Type,
        isOptedIn: item.SmsOptIn,
      }));
      setPhoneNumbers(formatted);
    }
  };


  const openDeleteConfirm = (id: string) => {
    setIdToDelete(id);
    setConfirmOpen(true);
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setIdToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!idToDelete) return;

    setConfirmOpen(false);

    const updatedPhones = phoneNumbers.filter(p => p.id !== idToDelete);
    setPhoneNumbers(updatedPhones);

    const result = await deleteJobseekerNumber(idToDelete);
    if (!result) {
      setPhoneNumbers(phoneNumbers);
    }
    setIdToDelete(null);
  };

  const updateOptInState = async (id: string, newOptIn: boolean) => {
    const targetPhone = phoneNumbers.find((p) => p.id === id);
    if (!targetPhone) return;

    const updatedPhones = phoneNumbers.map((p) =>
      p.id === id ? { ...p, isOptedIn: newOptIn } : p
    );
    setPhoneNumbers(updatedPhones);

    const payload = {
      phoneNumber: {
        type: targetPhone.type,
        countryCode: targetPhone.countryCode,
        number: targetPhone.number,
      },
      smsOptIn: newOptIn,
      active: true,
    };

    const result = await updateJobseekerNumber(id, payload);
    if (!result) {
      setPhoneNumbers(phoneNumbers);
    }

  };

  return (
    <>
      <Dialog
        open={openPopIn}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1.5,
            mb: 1,
          }}
        >
          Notification Settings
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress size={32} />
            </div>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Manage your phone numbers and notification preferences below:
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  marginBottom: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                  },
                }}
              >
                + Add Phone Number
              </Button>


              <List
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                  p: 0.5,
                  mt: 2,
                  overflow: 'hidden',
                }}
              >
                {phoneNumbers.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No phone numbers registered yet." />
                  </ListItem>
                ) : (
                  phoneNumbers.map((phone) => (
                    <ListItem
                      key={phone.id}
                      divider
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        py: 1,
                        px: 2,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {phone.number} (
                            {phone.type
                              ? phone.type.charAt(0).toUpperCase() + phone.type.slice(1).toLowerCase()
                              : ''}
                            )
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontSize: '14px' }}
                          >
                            Notifications: {phone.isOptedIn ? 'ON' : 'OFF'}
                          </Typography>
                        }
                        sx={{
                          flex: '1 1 auto',
                          minWidth: 0,
                          mr: 2,
                        }}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.2,
                          flexShrink: 0,
                          ml: 'auto',
                        }}
                      >
                        <Switch
                          checked={phone.isOptedIn}
                          onChange={() => handleToggle(phone.id)}
                          color="primary"
                          size="medium"
                          sx={{
                            transform: 'scale(0.9)',
                            '& .MuiSwitch-thumb': { width: 20, height: 20 },
                          }}
                        />

                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => openDeleteConfirm(phone.id)}
                          sx={{
                            cursor: 'pointer',
                            color: 'error.main',
                            ml: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 0, 0, 0.08)',
                              transform: 'scale(1.05)',
                            },
                            '& svg': { fontSize: 22 },
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </ListItem>

                  ))
                )}
              </List>

            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* ✅ Add Phone Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            overflow: 'hidden',
            minHeight: '0',
            transition: 'min-height 0.4s ease, padding 0.4s ease',
          },
        }}
      >
        <DialogTitle>Add New Phone Number</DialogTitle>

        <DialogContent
          dividers
          sx={{
            pt: 2,
            pb: 6,
            position: 'relative',
            overflow: 'hidden',
            transition: 'padding 0.4s ease',
          }}
        >
          <Stack spacing={1}>
            {/* Phone Type Dropdown */}
            <div style={{ width: '100%', position: 'relative', fontFamily: 'sans-serif' }}>
              <label
                htmlFor="phone-type"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  color: '#333',
                }}
              >
                Phone Type
                <div
                  onClick={() => setTypeOpen((prev) => !prev)}
                  style={{
                    height: '38px',
                    border: '1px solid #c4c4c4',
                    borderRadius: '4px',
                    padding: '0 10px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.95rem',
                    userSelect: 'none',
                  }}
                >
                  <span>{options.find((opt) => opt.value === newType)?.label}</span>
                  <span style={{ fontSize: '10px' }}>{typeOpen ? '▲' : '▼'}</span>
                </div>

                {typeOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      borderTop: 'none',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      zIndex: 100,
                    }}
                  >
                    {options.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setNewType(option.value);
                          setTypeOpen(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          margin: 1,
                          border: '1px solid #eee',
                          backgroundColor:
                            newType === option.value ? '#e6f2ff' : 'white',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) =>
                          ((e.target as HTMLDivElement).style.backgroundColor = '#f5f5f5')
                        }
                        onMouseLeave={(e) =>
                        ((e.target as HTMLDivElement).style.backgroundColor =
                          newType === option.value ? '#e6f2ff' : 'white')
                        }
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </label>
            </div>

            {/* Phone Input */}
            <label
              htmlFor="phone-number"
              style={{
                display: 'block',
                fontSize: '14px',
                color: '#333',
              }}
            >
              Phone Number
              <PhoneInput
                country={'us'}
                disableDropdown={true}
                buttonStyle={{
                  display: 'none',
                }}
                value={newNumber}
                onChange={(phone: string, countryData: CountryData | {}) => {
                  const data = countryData as CountryData;
                  setNewCountryCode(`+${data.dialCode}`);
                  setNewNumber(phone);
                }}
                inputProps={{
                  name: 's',
                  required: true,
                  autoFocus: true,
                }}
                containerStyle={{
                  width: '100%',
                  position: 'relative',
                }}
                inputStyle={{
                  width: '100%',
                  height: 38,
                  paddingLeft: '10px',
                  fontSize: '1rem',
                  borderRadius: 4,
                  borderColor: '#c4c4c4',
                  outline: 'none',
                }}
                specialLabel=""
              />
            </label>
          </Stack>

          {/* SMS Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={newOptIn}
                onChange={() => setNewOptIn(!newOptIn)}
                color="primary"
                size="medium"
              />
            }
            label="Enable SMS Notifications"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 24,
              fontSize: 14,
              color: '#333',
              alignItems: 'center',
              transform: 'scale(0.9)',
              '& .MuiSwitch-thumb': {
                width: 20,
                height: 20,
              },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
          }}
        >
          <Button
            onClick={() => setAddDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              minWidth: '90px',
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.23)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: 'rgba(0, 0, 0, 0.4)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddNewNumber}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              minWidth: '90px',
              boxShadow: '0 2px 5px rgba(33, 150, 243, 0.25)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { boxShadow: '0 3px 8px rgba(33, 150, 243, 0.35)' },
            }}
          >
            Add
          </Button>
        </DialogActions>


      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 460,
            borderRadius: 3,
            p: 0,
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.1rem',
            pb: 1,
            pt: 2,
            px: 3,
          }}
        >
          Confirm Delete
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.95rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            Are you sure you want to delete this phone number?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              minWidth: '90px',
              borderColor: '#bdbdbd',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#9e9e9e',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              py: 0.8,
              minWidth: '90px',
              boxShadow: '0 2px 6px rgba(244, 67, 54, 0.3)',
              transition: 'all 0.25s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 10px rgba(244, 67, 54, 0.4)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <OptOutConfirmation
        open={confirmOptOutOpen}
        onClose={() => {
          setConfirmOptOutOpen(false);
          setIdToOptOut(null);
        }}
        onConfirm={async () => {
          if (idToOptOut) {
            await updateOptInState(idToOptOut, false);
          }
          setConfirmOptOutOpen(false);
          setIdToOptOut(null);
        }}
      />

      <OptInConfirmation
        open={confirmOptInOpen}
        onClose={() => {
          setConfirmOptInOpen(false);
          setIdToOptIn(null);
        }}
        onConfirm={async () => {
          if (idToOptIn) {
            await updateOptInState(idToOptIn, true);
          }
          setConfirmOptInOpen(false);
          setIdToOptIn(null);
        }}
        phoneNumber={phoneNumbers.find(p => p.id === idToOptIn)?.number}
        phoneType={phoneNumbers.find(p => p.id === idToOptIn)?.type}
      />

    </>
  );
};

export default Notifications;
